/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CustomerHeader from '../components/CustomerHeader';
import './ProviderOnboardingPage.css';

const ProviderOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    category: '',
    experience: '',
    location: '',
    languages: [] as string[],
    skills: [] as string[],
    price: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const categories = [
    { id: 'plumbing', name: 'Plumbing', nameHi: 'प्लंबिंग', emoji: '🔧' },
    { id: 'electrical', name: 'Electricals', nameHi: 'इलेक्ट्रिकल', emoji: '⚡' },
    { id: 'painting', name: 'Painting', nameHi: 'पेंटिंग', emoji: '🎨' },
    { id: 'carpenter', name: 'Carpenter', nameHi: 'बढ़ई', emoji: '🪚' },
    { id: 'mason', name: 'Mason / Building', nameHi: 'राजमिस्त्री', emoji: '🧱' },
    { id: 'tiles-flooring', name: 'Tiles & Flooring', nameHi: 'टाइल्स और फर्श', emoji: '🔲' },
    { id: 'waterproofing', name: 'Waterproofing', nameHi: 'वॉटरप्रूफिंग', emoji: '💧' },
    { id: 'pest-control', name: 'Pest Control', nameHi: 'कीट नियंत्रण', emoji: '🐛' },
    { id: 'ac-fridge-repair', name: 'AC / Fridge Repair', nameHi: 'एसी / फ्रिज मरम्मत', emoji: '❄️' },
    { id: 'ro-water-service', name: 'RO Water Service', nameHi: 'आरओ वाटर सर्विस', emoji: '💦' },
    { id: 'event-decorators', name: 'Event Decorators', nameHi: 'इवेंट डेकोरेटर्स', emoji: '🎉' },
    { id: 'tent-stage', name: 'Tent & Stage', nameHi: 'तंबू और स्टेज', emoji: '⛺' },
    { id: 'drivers', name: 'Drivers', nameHi: 'ड्राइवर', emoji: '🚗' },
    { id: 'goods-transport', name: 'Goods Transport', nameHi: 'माल परिवहन', emoji: '🚚' },
    { id: 'packers-movers', name: 'Packers & Movers', nameHi: 'पैकर्स और मूवर्स', emoji: '📦' },
    { id: 'construction-labor', name: 'Construction Labor', nameHi: 'निर्माण श्रम', emoji: '👷' },
    { id: 'house-helpers', name: 'House Helpers', nameHi: 'घरेलू सहायक', emoji: '🏠' },
    { id: 'loaders-unloaders', name: 'Loaders / Unloaders', nameHi: 'लोडर / अनलोडर', emoji: '📦' },
    { id: 'security-guards', name: 'Security Guards', nameHi: 'सुरक्षा गार्ड', emoji: '🛡️' },
    { id: 'home-cleaning', name: 'Home Cleaning', nameHi: 'घर की सफाई', emoji: '🧹' },
    { id: 'tank-cleaning', name: 'Tank Cleaning', nameHi: 'टैंक सफाई', emoji: '💧' },
    { id: 'sofa-carpet', name: 'Sofa & Carpet', nameHi: 'सोफा और कालीन', emoji: '🛋️' },
    { id: 'computer-repair', name: 'Computer Repair', nameHi: 'कंप्यूटर मरम्मत', emoji: '💻' },
    { id: 'cctv-installation', name: 'CCTV Installation', nameHi: 'सीसीटीवी स्थापना', emoji: '📹' },
    { id: 'wifi-setup', name: 'Wi-Fi Setup', nameHi: 'वाई-फाई सेटअप', emoji: '📶' },
    { id: 'beautician', name: 'Beautician', nameHi: 'ब्यूटीशियन', emoji: '💄' },
    { id: 'tailor', name: 'Tailor', nameHi: 'दर्जी', emoji: '✂️' },
    { id: 'laundry-ironing', name: 'Laundry / Ironing', nameHi: 'लॉन्ड्री / आयरनिंग', emoji: '👔' }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      navigate('/services/onboarding-success');
    }, 2000);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="onboarding-step">
            <h2 className="step-title">
              {language === 'hi' ? 'बुनियादी जानकारी' : 'Basic Information'}
            </h2>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'नाम' : 'Full Name'} *
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                placeholder={language === 'hi' ? 'अपना नाम दर्ज करें' : 'Enter your name'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'फोन नंबर' : 'Phone Number'} *
              </label>
              <input
                type="tel"
                className="form-input"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
                placeholder="+91 98765 43210"
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'स्थान' : 'Location'} *
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                required
                placeholder={language === 'hi' ? 'शहर, क्षेत्र' : 'City, Area'}
              />
            </div>
            <button
              type="button"
              className="next-btn"
              onClick={() => setStep(2)}
              disabled={!formData.name || !formData.phone || !formData.location}
            >
              {language === 'hi' ? 'अगला' : 'Next'} →
            </button>
          </div>
        );

      case 2:
        return (
          <div className="onboarding-step">
            <h2 className="step-title">
              {language === 'hi' ? 'सेवा विवरण' : 'Service Details'}
            </h2>
            <p className="step-subtitle">
              {language === 'hi' ? 'अपनी सेवा के बारे में बताएं' : 'Tell us about your service'}
            </p>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'सेवा श्रेणी' : 'Service Category'} *
              </label>
              <p className="form-hint">
                {language === 'hi' 
                  ? 'अपनी विशेषज्ञता का सबसे अच्छा वर्णन करने वाली सेवा श्रेणी चुनें'
                  : 'Select the service category that best describes your expertise'}
              </p>
              <div className="category-grid">
                {categories.map((cat) => (
                  <label 
                    key={cat.id} 
                    className={`category-option ${formData.category === cat.id ? 'category-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={cat.id}
                      checked={formData.category === cat.id}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                    />
                    <span className="category-emoji">{cat.emoji}</span>
                    <span className="category-name">{language === 'hi' ? cat.nameHi : cat.name}</span>
                  </label>
                ))}
              </div>
              <p className="category-hint">
                {language === 'hi' 
                  ? `${categories.length} सेवा श्रेणियां दिखा रहे हैं। सभी विकल्प देखने के लिए स्क्रॉल करें।`
                  : `Showing ${categories.length} service categories. Scroll to see all options.`}
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'अनुभव' : 'Experience'} *
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.experience}
                onChange={(e) => handleInputChange('experience', e.target.value)}
                required
                placeholder={language === 'hi' ? 'उदा: 5 वर्ष' : 'e.g., 5 years'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'कीमत' : 'Price Range'} *
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                required
                placeholder={language === 'hi' ? 'उदा: ₹300-500' : 'e.g., ₹300-500'}
              />
            </div>
            <div className="step-actions">
              <button
                type="button"
                className="back-btn"
                onClick={() => setStep(1)}
              >
                ← {language === 'hi' ? 'वापस' : 'Back'}
              </button>
              <button
                type="button"
                className="next-btn"
                onClick={() => setStep(3)}
                disabled={!formData.category || !formData.experience || !formData.price}
              >
                {language === 'hi' ? 'अगला' : 'Next'} →
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="onboarding-step">
            <h2 className="step-title">
              {language === 'hi' ? 'अतिरिक्त जानकारी' : 'Additional Information'}
            </h2>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'विवरण' : 'Description'}
              </label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                placeholder={language === 'hi' 
                  ? 'अपनी सेवाओं के बारे में बताएं...' 
                  : 'Tell us about your services...'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                {language === 'hi' ? 'भाषाएं' : 'Languages'}
              </label>
              <div className="checkbox-group">
                {['Hindi', 'English', 'Punjabi', 'Haryanvi'].map((lang) => (
                  <label key={lang} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={formData.languages.includes(lang)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleInputChange('languages', [...formData.languages, lang]);
                        } else {
                          handleInputChange('languages', formData.languages.filter(l => l !== lang));
                        }
                      }}
                    />
                    <span>{lang}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="step-actions">
              <button
                type="button"
                className="back-btn"
                onClick={() => setStep(2)}
              >
                ← {language === 'hi' ? 'वापस' : 'Back'}
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading 
                  ? (language === 'hi' ? 'सबमिट हो रहा है...' : 'Submitting...')
                  : (language === 'hi' ? 'सबमिट करें' : 'Submit')
                }
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate('/services')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <div className="provider-onboarding-page">

      <main className="onboarding-main">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(step / 3) * 100}%` }}
          ></div>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form">
          {renderStep()}
        </form>
      </main>
      </div>
    </>
  );
};

export default ProviderOnboardingPage;








